/**
 * kit.tsx — the GPS Clinics UI primitives (theme-variable based).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY one kit: every surface (journey, auth, checkout, dashboard) shares the
 * same visual grammar — brand header, slim progress rail, question card,
 * option buttons, labelled fields, sticky action bar, alerts and a retryable
 * error state. Centralising them keeps the whole portal consistent and on the
 * approved GPS design. Every colour/space/radius is a `var(--gps-*)` token, so
 * these components re-skin for dark mode with no JS. Design rules baked in:
 *   - ≥44px tap targets on every interactive control.
 *   - Terracotta is reserved for the `cta` Button variant (the single
 *     conversion action per screen); green `primary` for in-flow navigation.
 *   - State is never colour-alone (ticks, weight, borders, text).
 *   - Native inputs only (bundle-lean; no date/select libraries).
 */
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { KeepCompoundsWhole } from './KeepCompoundsWhole';
import { RequiredMark } from './RequiredMark';

/* ── Brand + page frame ─────────────────────────────────────────────────── */

/**
 * BrandLockup — the header logo lockup shown on every surface: the real GP
 * Service wordmark on a brand-green rounded tile, a subtle dot separator, then
 * the clinic name ("Weight Loss Clinic") in muted body type.
 *
 * The wordmark asset (public/gps-logo.png) is a WHITE "The GP Service" mark —
 * it disappears on this portal's light header, so it sits on a brand-green
 * pill (matching the favicon treatment) to stay legible. TODO: if design later
 * supplies a dark/colour logo variant, drop the green-tile wrapper and render
 * the asset directly on the light background.
 *
 * Accessible: the whole lockup is exposed to assistive tech as a single image
 * with the label "GP Service — Weight Loss Clinic" (its visual pieces —
 * including the `<img>` itself, which carries an empty `alt` — are hidden from
 * AT to avoid a stuttered reading). Responsive: below 420px the "· Weight Loss
 * Clinic" text drops entirely, keeping just the logo tile.
 */
export function BrandLockup({
  clinicName = 'Weight Loss Clinic',
  ground = 'dark',
}: {
  clinicName?: string;
  /**
   * Which surface the mark sits on. 'dark' (default): the WHITE wordmark PNG
   * renders directly on the deep-green patient header. 'light' (UX Wave 7B —
   * the staff masthead's white header block): the white asset would vanish, so
   * the SAME asset is used as a CSS mask over the brand green — the exact mark
   * silhouette in an exact token colour, no second logo file.
   */
  ground?: 'dark' | 'light';
}) {
  // The patient header bar is deep green (see SiteHeader in ui/chrome.tsx), so
  // the WHITE "The GP Service" wordmark (public/gps-logo.png) sits directly on
  // it — no tile — centred, matching the GPS v3.0.0 header. Exposed to AT as
  // one image named "GP Service — Weight Loss Clinic"; the clinic name is not
  // repeated as visible text (the v3 header is the logo alone).
  if (ground === 'light') {
    // The PNG is 239×114 — the fixed box keeps the mask's contain-fit exact.
    const height = 36;
    const width = Math.round((height * 239) / 114);
    const mask: CSSProperties = {
      display: 'block',
      width: `${width}px`,
      height: `${height}px`,
      background: 'var(--gps-primary-dark)',
      WebkitMaskImage: 'url(/gps-logo.png)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      WebkitMaskPosition: 'center',
      maskImage: 'url(/gps-logo.png)',
      maskRepeat: 'no-repeat',
      maskSize: 'contain',
      maskPosition: 'center',
    };
    return (
      <span role="img" aria-label={`GP Service — ${clinicName}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span aria-hidden="true" style={mask} />
      </span>
    );
  }
  return (
    <span role="img" aria-label={`GP Service — ${clinicName}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <img src="/gps-logo.png" alt="" style={{ width: '120px', height: 'auto', display: 'block' }} />
    </span>
  );
}

/**
 * Page frame: a centred content column. `narrow` tightens the column for
 * single-column flows (auth, journey) — 34rem, which a journey surface widens
 * to fit its 40rem desktop column through a custom property on its
 * `gps-journey-root` class (see the note on `<main>` below).
 *
 * The brand header used to live here, per-page — it now lives ONCE in the
 * global `AppShell` (see ui/chrome.tsx's `SiteHeader`), so every surface
 * shares a single persistent header (logo-as-home-link + the "Speak to a
 * clinician" CTA) instead of each `PageFrame` re-rendering its own copy.
 */
export function PageFrame({
  children,
  narrow = false,
  bare = false,
  className,
}: {
  children: ReactNode;
  narrow?: boolean;
  /** Render the content DIRECTLY (no `<main>`, no max-width, no padding) — for a
   * surface already inside a shell that owns the `<main>` + gutters (the patient
   * portal's SideNavLayout, 2026-07-25). Avoids a nested `<main>` and lets the
   * content run FULL-WIDTH like the staff consoles. Ignores `narrow`. */
  bare?: boolean;
  /** A class on the frame's wrapper. The patient journey passes `gps-journey-root`
   * (see tokens.css "Journey look and feel") — the ONE hook that scopes the
   * journey's flat ivory ground (#f6f4ef) and ink overrides to journey surfaces
   * only. */
  className?: string;
}) {
  if (bare) return <>{children}</>;
  return (
    // `--gps-frame-ground` lets a surface whose page ground is painted OUTSIDE
    // this wrapper (the journey's ivory and its faint top wash, on AppShell)
    // make this wrapper transparent. Absent everywhere else, so every other
    // PageFrame keeps its flat `--gps-bg`.
    <div className={className} style={{ background: 'var(--gps-frame-ground, var(--gps-bg))' }}>
      <main
        style={{
          // `--gps-frame-narrow-max` is defined ONLY on `.gps-journey-root`
          // (tokens.css, L1 2026-09-24): the journey's classic column is 40rem
          // at >=880px and this main has to hold it plus its gutters. Anywhere
          // else it is undefined and the fallback is the old literal, so /auth
          // and the role gates still compute exactly 34rem.
          maxWidth: narrow ? 'var(--gps-frame-narrow-max, 34rem)' : '1100px',
          margin: '0 auto',
          padding: 'var(--gps-s-5) var(--gps-s-4) var(--gps-s-6)',
        }}
      >
        {children}
      </main>
    </div>
  );
}

/* ── Flow layout: appbar / progress / sticky action ─────────────────────── */

/** Slim top progress rail (0–100). Announces progress to assistive tech.
 * `gps-progress` / `gps-progress-fill` are class hooks: the journey re-draws the
 * rail as a 3px line (tokens.css, P7). Nothing outside a journey root styles them. */
export function ProgressRail({ percent, label }: { percent: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <div
      className="gps-progress"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Progress'}
      style={{
        flex: 1,
        height: '6px',
        borderRadius: 'var(--gps-r-pill)',
        background: 'var(--gps-primary-light)',
        overflow: 'hidden',
      }}
    >
      <div
        className="gps-progress-fill"
        style={{
          width: `${clamped}%`,
          height: '100%',
          background: 'linear-gradient(90deg, var(--gps-primary), var(--gps-primary-dark))',
          borderRadius: 'inherit',
          transition: 'width .25s ease',
        }}
      />
    </div>
  );
}

/** Circular back control (compact 32px face — 2026-07-21 restyle). */
export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="gps-back-btn"
      onClick={onClick}
      aria-label="Back"
      style={{
        width: '32px',
        height: '32px',
        borderRadius: 'var(--gps-r-pill)',
        border: '1px solid var(--gps-border-strong)',
        background: 'var(--gps-surface)',
        color: 'var(--gps-text)',
        display: 'grid',
        placeItems: 'center',
        fontSize: '16px',
        cursor: 'pointer',
        flex: 'none',
      }}
    >
      <span className="gps-back-arrow" aria-hidden="true" style={{ display: 'inline-flex' }}>
        <ChevronLeftIcon size={14} />
      </span>
    </button>
  );
}

/** The flow app-bar: optional back button, progress rail, optional step count. */
export function AppBar({
  onBack,
  percent,
  step,
  progressLabel,
}: {
  onBack?: () => void;
  percent?: number;
  step?: string;
  progressLabel?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gps-s-3)',
        marginBottom: 'var(--gps-s-4)',
        minHeight: '44px',
      }}
    >
      {onBack ? <BackButton onClick={onBack} /> : null}
      {typeof percent === 'number' ? <ProgressRail percent={percent} label={progressLabel} /> : null}
      {step ? (
        <span
          style={{
            fontSize: '12px',
            color: 'var(--gps-muted)',
            fontVariantNumeric: 'tabular-nums',
            flex: 'none',
          }}
        >
          {step}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Sticky action bar. On small screens it pins to the bottom of the viewport so
 * the primary action is always reachable; on larger screens it sits inline.
 */
export function StickyAction({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        marginTop: 'var(--gps-s-5)',
        // Clear the iOS home indicator: keep the base padding, or the safe-area
        // inset when it is larger (E3 #11).
        padding: 'var(--gps-s-3) 0',
        paddingBottom: 'max(var(--gps-s-3), env(safe-area-inset-bottom))',
        background:
          'linear-gradient(to top, var(--gps-bg) 62%, transparent)',
        // A subtle top hairline so the bar reads as floating cleanly over the
        // content it scrolls above.
        boxShadow: '0 -1px 0 var(--gps-border)',
      }}
    >
      {children}
    </div>
  );
}

/* ── Typographic primitives ─────────────────────────────────────────────── */

/* ── Typographic primitives: class hooks ─────────────────────────────────
 * `gps-jux-eyebrow`, `gps-jux-title` (+ `gps-jux-display` on a display-size
 * title), `gps-jux-help` and `gps-jux-field-label` are HOOKS for the journey's
 * premium type (tokens.css, P7 — every rule sits under `.gps-journey-root`).
 * The inline styles below are unchanged, so a staff console, /auth or the
 * patient portal renders exactly as before: nothing outside a journey root
 * styles these classes. Keyed on a class, never on an inline-style substring —
 * a harmless reorder of a style object must not silently drop the look. */

/** Small terracotta eyebrow above a title. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p
      className="gps-jux-eyebrow"
      style={{
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: 'var(--gps-eyebrow)',
        margin: '0 0 6px',
      }}
    >
      {children}
    </p>
  );
}

/** Screen/question title in the display face. */
export function Title({
  children,
  as = 'h1',
  size = '28px',
  id,
  tabIndex,
}: {
  children: ReactNode;
  as?: 'h1' | 'h2';
  size?: string;
  /** Optional id so a group or region can be named by this heading (aria-labelledby). */
  id?: string;
  /**
   * Optional, and only ever `-1`: lets a screen hand the keyboard to its heading
   * when it replaces the control that had focus (a removed focused element drops
   * focus to `<body>`). Never a Tab stop. Absent on every older call site.
   */
  tabIndex?: -1;
}) {
  const Tag = as;
  // A DISPLAY heading is one at the default size or larger (28px+). The journey
  // sets those in light Fraunces at 36/30px; a title that chose a smaller size
  // (a section h2, the Result verdict) keeps it and only drops to 400.
  const display = Number.parseFloat(size) >= 28;
  return (
    <Tag
      id={id}
      tabIndex={tabIndex}
      className={display ? 'gps-jux-title gps-jux-display' : 'gps-jux-title'}
      style={{ fontSize: size, margin: '2px 0 8px', color: 'var(--gps-text)' }}
    >
      {children}
    </Tag>
  );
}

/** Muted helper line under a title. */
export function Help({ children, style, id }: { children: ReactNode; style?: CSSProperties; id?: string }) {
  // `id` is optional and absent on every older call site (no attribute rendered),
  // so a control can point `aria-describedby` at a help line where it needs to.
  return (
    <p
      id={id}
      className="gps-jux-help"
      style={{ color: 'var(--gps-muted)', fontSize: '16px', lineHeight: 1.5, margin: '0 0 18px', ...style }}
    >
      {children}
    </p>
  );
}

/** Centred trust/reassurance microcopy under an action. */
export function Microcopy({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        textAlign: 'center',
        color: 'var(--gps-muted)',
        fontSize: '12.5px',
        margin: '10px 0 0',
      }}
    >
      {children}
    </p>
  );
}

/* ── Buttons ────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'cta' | 'ghost';

const BUTTON_BG: Record<ButtonVariant, string> = {
  primary: 'var(--gps-btn-primary)',
  cta: 'var(--gps-btn-secondary)',
  ghost: 'transparent',
};

/**
 * The shared button style. Extracted so a react-router `<Link>` can be styled
 * as a button (a real <button> must never be nested inside an <a>).
 *
 * 2026-07-21 restyle: dedicated button fill tokens, 6px radius, 44px min
 * height, medium weight — hover is a fill swap only (see tokens.css).
 */
export function buttonStyle(variant: ButtonVariant = 'primary', disabled = false): CSSProperties {
  const isGhost = variant === 'ghost';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    boxSizing: 'border-box',
    border: isGhost ? '1.5px solid var(--gps-border-strong)' : '0',
    borderRadius: 'var(--gps-btn-radius)',
    padding: '11px 24px',
    minHeight: '44px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 500,
    textDecoration: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    background: BUTTON_BG[variant],
    color: isGhost ? 'var(--gps-green-text)' : 'var(--gps-on-accent)',
  };
}

/**
 * The shared class list that layers hover feedback onto a button — a
 * background fill swap only (no shadow/lift/scale — 2026-07-21 restyle), plus
 * the desktop-only arrow slide for a {@link ButtonArrow} child. The rules live
 * in tokens.css so a react-router `<Link>` styled with {@link buttonStyle} can
 * opt in with the same class. `variant` picks the hover fill. Any caller-passed
 * className is preserved.
 */
export function buttonClass(variant: ButtonVariant = 'primary', className?: string): string {
  return ['gps-btn', `gps-btn-${variant}`, className].filter(Boolean).join(' ');
}

/**
 * The standard forward-arrow icon for buttons (2026-07-21 restyle) — ONE
 * shared SVG instead of unicode `→` in label text. 14×14 (matches the 14px
 * button label), `currentColor`, and never shrinks in the button's flex row.
 * Decorative (`aria-hidden`): the button's visible label carries the accessible
 * name. On desktop pointers the `.gps-btn-arrow` CSS (tokens.css) slides it
 * ~4px right on button hover; the button itself stays stationary.
 */
export function ButtonArrow() {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 20 21"
      width={14}
      height={14}
      xmlns="http://www.w3.org/2000/svg"
      className="gps-btn-arrow"
      style={{ width: 14, height: 14, flexShrink: 0 }}
    >
      <polygon
        fill="currentColor"
        points="16.172 9 10.101 2.929 11.515 1.515 20 10 19.293 10.707 11.515 18.485 10.101 17.071 16.172 11 0 11 0 9"
      />
    </svg>
  );
}

/**
 * The action button. `cta` is coral secondary — reserved for the single
 * conversion action per screen; `primary` is green for in-flow navigation;
 * `ghost` is a bordered secondary. Min height 44px.
 * Hover feedback rides the `gps-btn*` classes (reduced-motion-gated by the
 * global kill in tokens.css). `arrow` appends {@link ButtonArrow}.
 */
export function Button({
  children,
  variant = 'primary',
  arrow = false,
  style,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; arrow?: boolean }) {
  // `gps-btn-block` marks the FULL-WIDTH button (buttonStyle's default, which a
  // caller keeps unless it passes its own width — "Change", "Edit plan" and the
  // dialog buttons pass `width: 'auto'`). A class hook, so the journey can make
  // its step-forward pill taller than a compact one (tokens.css, P7) without
  // matching an inline-style substring. No rule outside a journey root reads it.
  const block = style?.width === undefined || style.width === '100%';
  // `aria-disabled` LOOKS disabled too (2026-09-25). A button that is waiting on
  // missing fields stays FOCUSABLE and PRESSABLE — so it can say what is missing
  // (the prescribe form's error summary) — but must not look ready. The hover
  // fill is withheld for it in tokens.css. A caller passing both attributes
  // (the checkout's pay control) renders exactly as before.
  const looksDisabled = Boolean(rest.disabled) || rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
  return (
    <button
      {...rest}
      className={buttonClass(variant, [block ? 'gps-btn-block' : null, className].filter(Boolean).join(' ') || undefined)}
      style={{ ...buttonStyle(variant, looksDisabled), ...style }}
    >
      {children}
      {arrow ? <ButtonArrow /> : null}
    </button>
  );
}

/* ── Shared icons (2026-07-21 restyle: SVGs replace unicode glyphs in shared
 * controls). Decorative (`aria-hidden`); surrounding text / aria-label carries
 * the accessible name. `currentColor` inherits the parent text colour. */

/** Tick / success marker (option select, trust chips, status markers). */
export function CheckIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M4 12.5l5.5 5.5L20 6.5" />
    </svg>
  );
}

/** Close / dismiss (drawer + dialog close controls). */
export function CloseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** Chevron-left — the shared "back" marker (BackButton). */
export function ChevronLeftIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M14.5 5l-7 7 7 7" />
    </svg>
  );
}

/** Downward chevron — the shared Select's dropdown indicator. */
export function ChevronDownIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 20 20"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Outline heart — the StopScreen's compassionate "not right for you" mark. */
export function HeartIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
      />
    </svg>
  );
}

/** Info marker for Alert tone="info". */
export function InfoIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
      <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M12 11v6" />
      <circle cx="12" cy="7.5" r="1.4" fill="currentColor" />
    </svg>
  );
}


/** Warning / danger marker for Alert tones (replaces a bare "!"). */
export function WarningIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
      />
      <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M12 9v4" />
      <circle cx="12" cy="17" r="1.2" fill="currentColor" />
    </svg>
  );
}

/** Link — insert-link control in note composers. */
export function LinkIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
      />
    </svg>
  );
}

/** Paperclip — attach control in note composers. */
export function PaperclipIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"
      />
    </svg>
  );
}

/** Coming-soon hourglass (ClinicSwitcher disabled rows). */
export function HourglassIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 2h12M6 22h12M8 2v3.5L12 10l4-4.5V2M8 22v-3.5L12 14l4 4.5V22"
      />
    </svg>
  );
}

/** Chevron-right — breadcrumb separators and similar decorative markers. */
export function ChevronRightIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' }}
    >
      <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M9.5 5l7 7-7 7" />
    </svg>
  );
}

/* ── Surfaces ───────────────────────────────────────────────────────────── */

/** Raised card surface. Pass `className="gps-card-interactive"` (E3 #5) for the
 * pointer-only hover lift on cards a patient can act on. */
export function Card({ children, style, className }: { children: ReactNode; style?: CSSProperties; className?: string }) {
  return (
    <section
      className={['gps-card', className].filter(Boolean).join(' ')}
      style={{
        background: 'var(--gps-surface)',
        // Flat modern-clean (2026-07-27): NO box-shadow on any card — the
        // hairline border alone keeps definition on white-on-white, with a
        // generous 20px inset and a rounded-lg corner.
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-lg)',
        padding: '20px',
        ...style,
      }}
    >
      {children}
    </section>
  );
}

/* ── Form controls ──────────────────────────────────────────────────────── */

const CONTROL_STYLE: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1.5px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-md)',
  padding: '13px 14px',
  font: 'inherit',
  fontSize: '16px',
  minHeight: '48px',
  background: 'var(--gps-surface)',
  color: 'var(--gps-text)',
};

/** Labelled field wrapper — binds the label to its control by id. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  hintId,
  errorId,
  required = false,
  errorAlert = true,
  children,
}: {
  label: string;
  htmlFor: string;
  /** Neutral helper copy (muted). Ignored when `error` is set. */
  hint?: string;
  /** Validation failure — danger colour + role="alert" so it is not mistaken for a hint. */
  error?: string;
  /**
   * Optional ids for the hint / error lines, so the control can name them in
   * `aria-describedby` (WCAG 3.3.1 — an error tied to its field, not only
   * announced). Absent → no id attribute, exactly as before.
   */
  hintId?: string;
  errorId?: string;
  /**
   * Marks the label with the kit's {@link RequiredMark} ("*", `aria-hidden`).
   * VISUAL ONLY — the caller must also put `aria-required="true"` on the
   * control, which is what a screen reader announces (see RequiredMark.tsx).
   * Absent → the label renders exactly as before.
   */
  required?: boolean;
  /**
   * `false` renders the error line WITHOUT `role="alert"`, for a form whose
   * error summary is the view's one assertive region; the error then reaches
   * assistive tech through the control's `aria-describedby` (pass `errorId`).
   * Default `true` — every existing caller is unchanged.
   */
  errorAlert?: boolean;
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 'var(--gps-s-3)' }}>
      <label
        htmlFor={htmlFor}
        className="gps-jux-field-label"
        style={{
          display: 'block',
          fontSize: '12.5px',
          fontWeight: 600,
          color: 'var(--gps-muted)',
          marginBottom: '6px',
        }}
      >
        {label}
        {required ? <RequiredMark /> : null}
      </label>
      {children}
      {error ? (
        <div
          id={errorId}
          role={errorAlert ? 'alert' : undefined}
          style={{ fontSize: '12.5px', color: 'var(--gps-danger-text)', marginTop: '4px', fontWeight: 500 }}
        >
          {error}
        </div>
      ) : hint ? (
        <div id={hintId} style={{ fontSize: '11px', color: 'var(--gps-muted)', marginTop: '4px' }}>{hint}</div>
      ) : null}
    </div>
  );
}

/** Native text/number/email input on the brand control style. */
export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...CONTROL_STYLE, ...props.style }} />;
}

/** Outline eye (password visible) — decorative; the toggle button owns the name. */
function EyeOpenIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Outline eye with slash (password hidden). */
function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M3 3l18 18" />
      <path d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-1.2" />
      <path d="M9.9 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17.3 17.3 0 0 1-3.2 4.4" />
      <path d="M6.1 6.1A17.5 17.5 0 0 0 2 12s3.5 7 10 7a10.4 10.4 0 0 0 4.1-.8" />
    </svg>
  );
}

/**
 * Password field with an in-field visibility toggle (eye icon).
 * Layout matches {@link TextInput} (same height / border) so toggling never
 * shifts surrounding form fields. The toggle is a real button (≥44px hit
 * target) with an updating aria-label; Enter in the input still submits the
 * parent form (button type="button").
 */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  disabled,
  name,
  style,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'block' }}>
      <input
        {...rest}
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        disabled={disabled}
        style={{
          ...CONTROL_STYLE,
          paddingRight: '48px',
          ...style,
        }}
      />
      <button
        type="button"
        tabIndex={0}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        disabled={disabled}
        onClick={() => setVisible((v) => !v)}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '48px',
          height: '100%',
          minHeight: '48px',
          display: 'grid',
          placeItems: 'center',
          border: 0,
          background: 'transparent',
          color: 'var(--gps-muted)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          padding: 0,
        }}
      >
        {visible ? <EyeOffIcon /> : <EyeOpenIcon />}
      </button>
    </span>
  );
}

/** Native textarea. */
export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} style={{ ...CONTROL_STYLE, minHeight: '5rem', ...props.style }} />;
}

/** Native select on the brand control style, with a custom chevron replacing
 * the browser's inconsistent native arrow. Native `<select>` has no reliable
 * cross-browser "open" event, so the chevron rotates 180° while the control is
 * focused and returns on blur. Semantics, keyboard behaviour and validation
 * are untouched — the chevron is decorative (`aria-hidden`, no pointer events).
 */
export function Select({ onFocus, onBlur, style, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  const [focused, setFocused] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'block' }}>
      <select
        {...props}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
        style={{
          ...CONTROL_STYLE,
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          paddingRight: '44px',
          ...style,
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          right: '16px',
          display: 'inline-flex',
          pointerEvents: 'none',
          color: props.disabled ? 'var(--gps-muted)' : 'var(--gps-text)',
          transform: focused ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
          transition: 'transform 300ms ease',
        }}
      >
        <ChevronDownIcon size={16} />
      </span>
    </span>
  );
}

/** The shape of an answer's control: a circle for one answer, a square for several. */
export type OptionControl = 'radio' | 'checkbox';

/**
 * A selectable answer option with a tick. Variants:
 *   - `exclusive` — the "none of these" option (pinned top, solid green edge).
 *   - `subtle`    — a lighter edge for a long option list.
 * Selection is shown three ways (border, tint, tick) — never colour-alone.
 *
 * `control` (journey premium look, CTO 2026-09-24) — see {@link ControlOption}.
 * Absent (every staff and portal call site) renders EXACTLY the option below,
 * byte for byte; the journey's questions pass it explicitly.
 */
export function OptionButton({
  children,
  description,
  selected = false,
  exclusive = false,
  subtle = false,
  disabled = false,
  showTick = true,
  highlighted = false,
  control,
  onClick,
}: {
  children: ReactNode;
  description?: string;
  selected?: boolean;
  exclusive?: boolean;
  subtle?: boolean;
  disabled?: boolean;
  showTick?: boolean;
  /** A subtle primary-tint ring drawing the eye to a stand-out option (the
   * "best value" plan term, E3 #6) — accessibility is carried by the option's
   * own visible Badge/label, never this ring alone. */
  highlighted?: boolean;
  /**
   * Draw the answer as a card with its control on the LEFT: a circle
   * (`'radio'`) for a question that takes one answer, a rounded square
   * (`'checkbox'`) for one that takes several. Explicit, never inferred from
   * the layout around the option. `showTick` and `subtle` do not apply — the
   * control is always drawn, so every label in a list starts at the same x
   * ("None of these apply" included).
   */
  control?: OptionControl;
  onClick: () => void;
}) {
  if (control) {
    return (
      <ControlOption
        control={control}
        description={description}
        selected={selected}
        exclusive={exclusive}
        disabled={disabled}
        highlighted={highlighted}
        onClick={onClick}
      >
        {children}
      </ControlOption>
    );
  }
  const edge = selected || exclusive
    ? 'var(--gps-primary)'
    : subtle
      ? 'var(--gps-border)'
      : 'var(--gps-border-strong)';
  // The selected inset and the highlight ring compose: a selected best-value
  // term shows both the green selection edge and the tint ring.
  const rings = [
    selected ? 'inset 0 0 0 1px var(--gps-primary)' : null,
    highlighted ? 'inset 0 0 0 2px var(--gps-primary-tint-strong)' : null,
  ].filter(Boolean);
  return (
    <button
      type="button"
      className="gps-option"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        textAlign: 'left',
        background: selected ? 'var(--gps-primary-light)' : 'var(--gps-surface)',
        border: `1.5px solid ${edge}`,
        boxShadow: rings.length > 0 ? rings.join(', ') : 'none',
        borderRadius: 'var(--gps-r-md)',
        padding: '15px 16px',
        marginBottom: '10px',
        minHeight: '56px',
        font: 'inherit',
        fontSize: '15.5px',
        fontWeight: exclusive ? 600 : 400,
        color: 'var(--gps-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span style={{ flex: 1 }}>
        <span style={{ fontWeight: exclusive ? 600 : 400 }}>{children}</span>
        {description ? (
          <span
            style={{
              display: 'block',
              fontSize: '13px',
              color: 'var(--gps-muted)',
              marginTop: '2px',
              fontWeight: 400,
            }}
          >
            {description}
          </span>
        ) : null}
      </span>
      {showTick ? (
        <span
          aria-hidden="true"
          style={{
            width: '22px',
            height: '22px',
            borderRadius: 'var(--gps-r-pill)',
            border: `2px solid ${selected ? 'var(--gps-primary)' : 'var(--gps-border-strong)'}`,
            background: selected ? 'var(--gps-primary)' : 'transparent',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: '13px',
            flex: 'none',
            // A small tick "pop" on select (scale 0.6→1) — reduced-motion users
            // get the global animation kill, so it lands instantly (E3 #2).
            animation: selected ? 'gps-tick-pop var(--gps-dur-fast) var(--gps-ease)' : undefined,
          }}
        >
          {selected ? <CheckIcon size={12} /> : null}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The journey's answer card — {@link OptionButton} with `control` (CTO
 * 2026-09-24, the premium look). The SAME button semantics as the classic
 * option: a `<button aria-pressed>`, named by its label, the control
 * `aria-hidden` — so roles, the accessible name, keyboard and the tap handler
 * are untouched; only the drawing moves.
 *
 * THE CONTROL IS FIRST IN THE DOM as well as on screen (left), so what is seen
 * and what is read agree. It is decorative: `aria-pressed` carries the state.
 *
 * PAINT IS CUSTOM PROPERTIES WITH THE CLASSIC VALUES AS FALLBACKS. The journey
 * root redefines `--gps-jux-option-*` (tokens.css, P7): a white card, a warm
 * hairline, a soft shadow, a 14px corner. Outside a journey root the fallbacks
 * resolve to today's option — white, the 4.08:1 control edge, no shadow — so a
 * caller that ever renders this elsewhere gets a sane option, never a bare
 * browser button.
 *
 * SELECTED = a deep-green edge, a FILLED control (a disc with a white dot, or a
 * square with a white tick) and a very light tint — three cues, never colour
 * alone. The edge is 1px + a 0.5px inset (1.5px together)
 * so the card never changes size when it is picked.
 */
function ControlOption({
  control,
  children,
  description,
  selected,
  exclusive,
  disabled,
  highlighted,
  onClick,
}: {
  control: OptionControl;
  children: ReactNode;
  description?: string;
  selected: boolean;
  exclusive: boolean;
  disabled: boolean;
  highlighted: boolean;
  onClick: () => void;
}) {
  const shadows = [
    selected ? 'inset 0 0 0 0.5px var(--gps-primary)' : null,
    highlighted ? 'inset 0 0 0 2px var(--gps-primary-tint-strong)' : null,
    // The fallback is a no-op shadow in a KEYWORD colour, never a hex: the
    // repo's markup specs assert no raw hex reaches inline styles.
    'var(--gps-jux-option-shadow, 0 0 transparent)',
  ].filter(Boolean);
  const round = control === 'radio';
  return (
    <button
      type="button"
      className="gps-option gps-option--control"
      data-control={control}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        width: '100%',
        textAlign: 'left',
        background: selected
          ? 'var(--gps-jux-option-bg-selected, var(--gps-primary-light))'
          : 'var(--gps-jux-option-bg, var(--gps-surface))',
        border: `1px solid ${selected ? 'var(--gps-primary)' : 'var(--gps-jux-option-edge, var(--gps-border-strong))'}`,
        boxShadow: shadows.join(', '),
        borderRadius: 'var(--gps-jux-option-radius, var(--gps-r-md))',
        padding: '16px 18px',
        marginBottom: '10px',
        minHeight: '58px',
        font: 'inherit',
        fontSize: '16px',
        fontWeight: 500,
        color: 'var(--gps-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="gps-option-control"
        aria-hidden="true"
        style={{
          width: '22px',
          height: '22px',
          flex: 'none',
          boxSizing: 'border-box',
          display: 'grid',
          placeItems: 'center',
          borderRadius: round ? 'var(--gps-r-pill)' : '6px',
          // The ring is the one thing that identifies an EMPTY control, so it
          // carries the 3:1 non-text contrast (1.4.11) — the card's hairline
          // does not have to.
          border: `1.5px solid ${selected ? 'var(--gps-primary)' : 'var(--gps-jux-control-ring, var(--gps-border-strong))'}`,
          // FILLED when picked — both shapes: a deep-green disc with a white
          // centre dot (one answer) or a deep-green square with a white tick
          // (several). White on #0a4a2e is 10.3:1.
          background: selected ? 'var(--gps-primary)' : 'var(--gps-surface)',
          color: 'var(--gps-on-accent)',
          // The same tick "pop" as the classic option; the global reduced-motion
          // kill lands it instantly.
          animation: selected ? 'gps-tick-pop var(--gps-dur-fast) var(--gps-ease)' : undefined,
        }}
      >
        {selected ? (
          round ? (
            <span
              style={{ width: '8px', height: '8px', borderRadius: 'var(--gps-r-pill)', background: 'var(--gps-on-accent)' }}
            />
          ) : (
            <CheckIcon size={13} />
          )
        ) : null}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: exclusive ? 600 : 500 }}>{children}</span>
        {description ? (
          <span
            style={{
              display: 'block',
              fontSize: '13.5px',
              color: 'var(--gps-muted)',
              marginTop: '2px',
              fontWeight: 400,
            }}
          >
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/** A required-consent checkbox row (label bound to the box). */
export function CheckboxRow({
  label,
  checked,
  disabled,
  describedBy,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  /**
   * Optional `aria-describedby` passthrough for a box whose surrounding prose
   * (an intro, a withdraw line) is part of what is being agreed to, but is not
   * the NAME of the box (a label names one box, not a paragraph about it) —
   * the Step-0 consent tick. Absent = unchanged, like `WeightPhotoPicker`'s
   * `style` passthrough, so the other 20+ call sites render byte-identically.
   */
  describedBy?: string;
  onChange: () => void;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      style={{
        display: 'flex',
        gap: 'var(--gps-s-3)',
        alignItems: 'flex-start',
        padding: '10px 0',
        color: 'var(--gps-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: '14.5px',
        lineHeight: 1.45,
      }}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-describedby={describedBy}
        onChange={onChange}
        style={{ width: '20px', height: '20px', marginTop: '1px', flex: 'none', accentColor: 'var(--gps-primary)' }}
      />
      <span>{label}</span>
    </label>
  );
}

/* ── Feedback ───────────────────────────────────────────────────────────── */

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

const ALERT_TOKENS: Record<AlertTone, { bg: string; text: string; icon: ReactNode }> = {
  info: { bg: 'var(--gps-info-bg)', text: 'var(--gps-info-text)', icon: <InfoIcon /> },
  success: { bg: 'var(--gps-info-bg)', text: 'var(--gps-success-text)', icon: <CheckIcon /> },
  warning: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)', icon: <WarningIcon /> },
  danger: { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)', icon: <WarningIcon /> },
};

/** Inline status message. An icon + text conveys tone (never colour-alone). */
export function Alert({
  tone = 'info',
  children,
  role,
  style,
  id,
  tabIndex,
}: {
  tone?: AlertTone;
  children: ReactNode;
  role?: 'status' | 'alert';
  style?: CSSProperties;
  /** Optional id so an input can point at the alert via aria-describedby. */
  id?: string;
  /**
   * Optional, and only ever `-1`: a programmatic focus target for a status line
   * that appears in place of the control that had focus — a live region inserted
   * with its text already in it is not reliably announced, a focused one is read.
   */
  tabIndex?: -1;
}) {
  const t = ALERT_TOKENS[tone];
  return (
    <div
      id={id}
      role={role}
      tabIndex={tabIndex}
      style={{
        display: 'flex',
        gap: 'var(--gps-s-2)',
        alignItems: 'flex-start',
        background: t.bg,
        color: t.text,
        borderRadius: 'var(--gps-r-md)',
        padding: '11px 13px',
        fontSize: '13.5px',
        lineHeight: 1.45,
        ...style,
      }}
    >
      <span aria-hidden="true" style={{ fontWeight: 700, flex: 'none' }}>
        {t.icon}
      </span>
      <span>{children}</span>
    </div>
  );
}

/** Inline validation error line (assertive). */
export function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px', margin: '8px 0' }}>
      {children}
    </p>
  );
}

/**
 * A rendered, retryable error state for a failed API call (TA-06). Never a
 * blank screen: it states what went wrong and offers a Retry action.
 */
export function ErrorState({
  message,
  onRetry,
  retryLabel = 'Try again',
}: {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div role="alert" style={{ textAlign: 'center', padding: 'var(--gps-s-5) 0' }}>
      <div
        aria-hidden="true"
        style={{
          width: '48px',
          height: '48px',
          borderRadius: 'var(--gps-r-pill)',
          background: 'var(--gps-warn-bg)',
          color: 'var(--gps-warn-text)',
          display: 'grid',
          placeItems: 'center',
          fontSize: '22px',
          margin: '0 auto var(--gps-s-3)',
        }}
      >
        !
      </div>
      <p style={{ color: 'var(--gps-text)', margin: '0 0 var(--gps-s-3)' }}>{message}</p>
      {onRetry ? (
        <div style={{ maxWidth: '16rem', margin: '0 auto' }}>
          <Button type="button" variant="primary" onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** A brand spinner with an accessible busy label. */
export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" style={{ textAlign: 'center', padding: 'var(--gps-s-5) 0' }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '4px solid var(--gps-border)',
          borderTopColor: 'var(--gps-primary)',
          borderRadius: '50%',
          animation: 'gps-spin 1s linear infinite',
        }}
      />
      <span className="gps-sr-only">{label}</span>
      <style>{'@keyframes gps-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

export type BadgeTone = 'neutral' | 'positive' | 'pending' | 'muted' | 'danger';

const BADGE_TOKENS: Record<BadgeTone, { bg: string; text: string }> = {
  neutral: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  positive: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  pending: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' },
  muted: { bg: 'var(--gps-surface-2)', text: 'var(--gps-muted)' },
  // Reuses the Alert danger tokens — for a genuinely bad state (e.g. a
  // card_declined failed payment) that must read as more severe than amber.
  danger: { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' },
};

/** A small status pill. The label text conveys state (never colour-alone). */
export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: BadgeTone }) {
  const t = BADGE_TOKENS[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        background: t.bg,
        color: t.text,
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        padding: '4px 10px',
        borderRadius: 'var(--gps-r-pill)',
      }}
    >
      {children}
    </span>
  );
}

/**
 * TrustStrip — a compact row of reassurance chips (E3 #9) shown on conversion
 * surfaces (Step-0 capture, the payment step). The wording is category-level
 * (R28-safe) and drawn from the footer's trust line — the visible text carries
 * the meaning, so the leading tick is decorative (`aria-hidden`). Rendered as a
 * list so assistive tech announces each cue (two or three) as its own item.
 *
 * `paymentCue` overrides the third chip on the payment step so Route 2
 * (immediate capture) does not promise "No charge until approved". `null` means
 * NO third chip (2026-09-26): the plan screen and the card step's hold route
 * already say when money moves, in full words, right beside the row ("No charge
 * until your treatment is approved." under the plan screen's button; the hold
 * box's "You only pay if a clinician approves"), and a chip repeating it was the
 * third telling the CTO asked to lose ("we are saying the same thing over and
 * over"). Omitted keeps the old default, for any caller that has not chosen.
 *
 * ⚠️ "GPhC-registered pharmacy", NEVER "GMC/GPhC pharmacy" (review, 2026-09-25).
 * A pharmacy registers with the GPhC only; the GMC registers doctors. The chip
 * said "GMC/GPhC pharmacy" until the Result screen reused this strip and the
 * claim reached more patients. The footer (`ui/chrome.tsx`) is the source:
 * clinicians "registered with the GMC or GPhC", dispensing by Pharmacy Xpress
 * "(GPhC-registered)". A regulatory-status claim must be exactly true (CAP 3.1,
 * DMCC Act 2024 misleading actions).
 */
const TRUST_CUES_BASE = ['UK clinicians', 'GPhC-registered pharmacy'] as const;
const TRUST_CUE_HOLD = 'No charge until approved';

export function TrustStrip({
  paymentCue = TRUST_CUE_HOLD,
  style,
}: {
  paymentCue?: string | null;
  /** Placement only (the plan rail's pinned foot tightens the margin); the chips' own look never changes. */
  style?: CSSProperties;
} = {}) {
  const cues: string[] = paymentCue === null ? [...TRUST_CUES_BASE] : [...TRUST_CUES_BASE, paymentCue];
  return (
    <ul
      aria-label="Why you can trust this service"
      style={{
        listStyle: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--gps-s-2)',
        margin: 'var(--gps-s-3) 0',
        padding: 0,
        ...style,
      }}
    >
      {cues.map((cue) => (
        <li
          key={cue}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--gps-primary-light)',
            color: 'var(--gps-green-text)',
            borderRadius: 'var(--gps-r-pill)',
            padding: '6px 12px',
            fontSize: '12.5px',
            fontWeight: 600,
          }}
        >
          <CheckIcon size={12} />
          {/* "GPhC-registered" stays on one line in a narrow pill. */}
          <span>
            <KeepCompoundsWhole text={cue} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The persistent "not for emergencies" safety line for public journey screens. */
export function EmergencyNote() {
  return (
    <Alert tone="info" style={{ marginBottom: 'var(--gps-s-4)' }}>
      In a medical emergency call <strong>999</strong>, or <strong>NHS 111</strong> for urgent
      advice. This service is not for emergencies.
    </Alert>
  );
}

/* ── Tables (admin-console redesign) ───────────────────────────────────────── */

/** Shared `<th>` style for the admin data tables (Users/Orders/Alerts). */
export const tableHeadCellStyle: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: 700,
  color: 'var(--gps-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid var(--gps-border-strong)',
  whiteSpace: 'nowrap',
};

/** Shared `<td>` style for the admin data tables (Users/Orders/Alerts). */
export const tableCellStyle: CSSProperties = {
  padding: '10px',
  borderBottom: '1px solid var(--gps-border)',
  color: 'var(--gps-text)',
  fontSize: '14px',
  verticalAlign: 'top',
};

/**
 * A horizontally-scrollable wrapper so a wide data table never breaks the page
 * layout on a narrow viewport (the table itself keeps its natural width).
 */
export function TableScroll({ children }: { children: ReactNode }) {
  return <div style={{ overflowX: 'auto' }}>{children}</div>;
}

/* ── Drawer (admin-console redesign) ───────────────────────────────────────── */

export type DrawerWidth = 'md' | 'lg' | 'xl' | 'twoThirds';

/** Panel widths — still `min(X, 100%)` so a narrow viewport never overflows. */
const DRAWER_MAX_WIDTH: Record<DrawerWidth, string> = {
  md: '28rem',
  lg: '44rem',
  xl: '64rem',
  // CRM deal drawer (2026-07-21): "use 2/3 of the screen when a deal is open"
  // — viewport-proportional, capped so an ultra-wide monitor stays readable.
  twoThirds: 'min(67vw, 90rem)',
};

/**
 * An accessible side drawer: `role="dialog"`, focus is moved into it on open,
 * Escape closes it, and a click on the overlay (outside the panel) closes it
 * too. Intentionally simple (no full focus trap) — the panel itself is
 * focused on open and Escape/overlay-click are the two closes callers need.
 *
 * LAYOUT (v2): the panel is a flex column — the title row (title + close
 * button) is a FROZEN, non-scrolling header bar (the title must never scroll
 * away under a long form), only the content area scrolls, and an optional
 * `footer` slot pins primary actions to the bottom, also non-scrolling.
 * `width` picks the panel's max width ('md' default keeps existing consumers
 * unchanged; the admin tabs opt into 'lg'/'xl' for real working room).
 * `title` accepts a ReactNode so a caller can freeze more than plain text in
 * the header (the callbacks ticket pins contact name + a status Badge there);
 * it still renders inside the labelling <h2>, so keep it name-like — the
 * node's text IS the dialog's accessible name.
 */
export function Drawer({
  open,
  onClose,
  title,
  width = 'md',
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  width?: DrawerWidth;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(19, 35, 27, 0.45)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: `min(${DRAWER_MAX_WIDTH[width]}, 100%)`,
          height: '100%',
          background: 'var(--gps-surface)',
          boxShadow: '-8px 0 24px rgba(19, 35, 27, 0.18)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* FROZEN header — never scrolls with the content below. */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--gps-s-3)',
            padding: 'var(--gps-s-3) var(--gps-s-4)',
            borderBottom: '1px solid var(--gps-border)',
            flexShrink: 0,
          }}
        >
          <h2 id={titleId} style={{ margin: 0, fontSize: '18px', color: 'var(--gps-text)' }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              minWidth: '44px',
              minHeight: '44px',
              border: 0,
              background: 'none',
              color: 'var(--gps-muted)',
              fontSize: '20px',
              lineHeight: 1,
              cursor: 'pointer',
              flex: 'none',
            }}
          >
            <CloseIcon />
          </button>
        </div>
        {/* Only the content scrolls. */}
        <div style={{ overflowY: 'auto', flex: 1, padding: 'var(--gps-s-4)' }}>{children}</div>
        {footer ? (
          <div
            style={{
              padding: 'var(--gps-s-3) var(--gps-s-4)',
              borderTop: '1px solid var(--gps-border)',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
